'use client'

import React, { useState, useEffect, useRef } from 'react'
import { Container } from '@quark/layout'
import { Text } from '@quark/typography'
import { Reveal } from '@quark/animation'
import { createScrollHandler } from '@utils/mobileScrollUtils'
import BusinessModals from '@components/BusinessModal/businessModals'
import BenefitsCard from '../Card'
import styles from '../../benefitsCarousal.module.scss'

interface HorizontalViewProps {
  title?: string
  benefitCards?: any[]
}

const HorizontalView = ({ title, benefitCards }: HorizontalViewProps) => {
  const [activeDotIndex, setActiveDotIndex] = useState<number>(0)
  const cardRefs = useRef<Array<HTMLDivElement | null>>([])
  const mobileScrollRef = useRef<HTMLDivElement | null>(null)
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const [formSnippet, setFormSnippet] = useState<string>('')

  useEffect(() => {
    if (!benefitCards || !mobileScrollRef.current) return

    const handleScroll = createScrollHandler(mobileScrollRef, cardRefs, setActiveDotIndex)

    mobileScrollRef.current.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()

    return () => {
      if (mobileScrollRef.current) {
        mobileScrollRef.current.removeEventListener('scroll', handleScroll)
      }
    }
  }, [benefitCards])

  return (
    <Container
      type="fluid"
      paddingTop={{ lg: 'spacing-72', md: 'spacing-56', sm: 'spacing-40' }}
      backgroundColor="base/overlay">
      <Container type="fluid">
        <Reveal as="span" animationProps={{ threshold: 0.1, animationDelayMs: 100 }}>
          <Container type="max-parent" paddingBottom={{ lg: 'spacing-48', md: 'spacing-32' }}>
            <Text
              as="h1"
              fontStyle={{ lg: 'um_text-5xl/bold', md: 'um_text-3xl/bold', sm: 'um_text-2xl/bold' }}
              color="base/primary"
              textAlign={{ lg: 'center', md: 'center', sm: 'center' }}
              dangerouslySetInnerHTML={{ __html: title || '' }}
            />
          </Container>
        </Reveal>
        <div ref={mobileScrollRef} className={styles.mobileScrollContainer}>
          {benefitCards?.map((card, index) => (
            <Container type="root" key={index}>
              <div
                ref={el => {
                  cardRefs.current[index] = el
                }}
                className={styles.mobileBenefitsCardContainer}>
                <BenefitsCard data={card} setIsModalOpen={setIsModalOpen} setFormSnippet={setFormSnippet} />
              </div>
            </Container>
          ))}
        </div>
        {benefitCards && benefitCards.length > 1 && (
          <div className={styles.dotsContainer}>
            {benefitCards.map((_, index) => (
              <div key={index} className={`${styles.dot} ${activeDotIndex === index ? styles.activeDot : ''}`} />
            ))}
          </div>
        )}
      </Container>
      <BusinessModals isModalOpen={isModalOpen} setIsModalOpen={setIsModalOpen} content={formSnippet} />
    </Container>
  )
}

export default HorizontalView
